import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isHovering: false,
      detailsOpen: false,
      errorType: null,
      errorLocation: null
    };
    
    // Store original error handlers
    this.originalErrorHandler = window.onerror;
    this.originalUnhandledRejectionHandler = window.onunhandledrejection;
  }

  static getDerivedStateFromError(error) {
    let errorType = 'Unknown';
    
    if (error instanceof TypeError) errorType = 'TypeError';
    else if (error instanceof ReferenceError) errorType = 'ReferenceError';
    else if (error instanceof RangeError) errorType = 'RangeError';
    else if (error.name === 'ChunkLoadError') errorType = 'NetworkError';
    else if (error.message.includes('NetworkError')) errorType = 'NetworkError';
    
    return { 
      hasError: true, 
      error,
      errorType,
      errorLocation: window.location.pathname
    };
  }

  componentDidMount() {
    // Catch all unhandled promise rejections (async errors)
    window.addEventListener('unhandledrejection', this.handleAsyncError);
    
    // Catch all runtime errors
    window.addEventListener('error', this.handleRuntimeError);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleAsyncError);
    window.removeEventListener('error', this.handleRuntimeError);
  }

  handleAsyncError = (event) => {
    event.preventDefault(); // Prevent default console error
    
    if (!this.state.hasError) {
      this.setState({
        hasError: true,
        error: event.reason || new Error('Async operation failed'),
        errorType: 'AsyncError',
        errorLocation: window.location.pathname
      });
    }
  };

  handleRuntimeError = (event) => {
    event.preventDefault(); // Prevent default console error
    
    if (!this.state.hasError) {
      this.setState({
        hasError: true,
        error: event.error || new Error(event.message),
        errorType: 'RuntimeError',
        errorLocation: window.location.pathname
      });
    }
  };

  componentDidCatch(error, errorInfo) {
    console.error("❌ Caught by ErrorBoundary:", error, errorInfo);
    this.setState({ errorInfo });
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    const errorData = {
      error: error.toString(),
      errorType: this.state.errorType,
      componentStack: errorInfo?.componentStack || 'No stack trace',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    console.log('Error reported:', errorData);
    // Example: sendToErrorTrackingService(errorData);
  };

  getUserFriendlyMessage() {
    const { errorType } = this.state;
    
    const messages = {
      TypeError: "A type error occurred. Please try again or contact support.",
      ReferenceError: "A reference error occurred. Our team has been notified.",
      RangeError: "A range error occurred. This typically happens with invalid values.",
      NetworkError: "Failed to load required resources. Please check your connection.",
      AsyncError: "An async operation failed. Please try again.",
      RuntimeError: "A runtime error occurred. We're working to fix it.",
      Unknown: "An unexpected error occurred. Our team has been notified."
    };

    return messages[errorType] || messages.Unknown;
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorType: null
    });
    
    if (this.props.onRetry) {
      this.props.onRetry();
    } else {
      window.location.reload();
    }
  };

  toggleDetails = () => {
    this.setState(prev => ({ detailsOpen: !prev.detailsOpen }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            height: '100vh',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            color: '#212529',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            padding: '2rem',
            zIndex: 50,
            overflow: 'hidden',
            textAlign: 'center',
            position: 'fixed',
            width: '100%'
          }}
        >
          {/* Animated background elements */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(220, 53, 69, 0.1)',
            filter: 'blur(40px)',
            animation: 'float 15s ease-in-out infinite alternate'
          }}></div>
          
          <div style={{
            position: 'absolute',
            bottom: '-50px',
            left: '-50px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(13, 110, 253, 0.1)',
            filter: 'blur(50px)',
            animation: 'float 20s ease-in-out infinite alternate-reverse'
          }}></div>

          <style>{`
            @keyframes float {
              0% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(20px, 20px) rotate(5deg); }
              100% { transform: translate(0, 0) rotate(0deg); }
            }

            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }

            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }

            @keyframes ripple {
              0% { transform: scale(0.8); opacity: 1; }
              100% { transform: scale(1.2); opacity: 0; }
            }

            .error-card {
              background: white;
              border-radius: 16px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.1);
              padding: 40px;
              max-width: 600px;
              width: 90%;
              position: relative;
              overflow: hidden;
              z-index: 10;
              transition: all 0.3s ease;
              border: 1px solid rgba(0,0,0,0.05);
            }

            .error-card:hover {
              transform: translateY(-5px);
              box-shadow: 0 15px 35px rgba(0,0,0,0.15);
            }

            .error-icon-container {
              position: relative;
              width: 100px;
              height: 100px;
              margin: 0 auto 25px;
            }

            .error-icon {
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
              color: white;
              border-radius: 50%;
              font-size: 40px;
              font-weight: bold;
              position: relative;
              z-index: 2;
              box-shadow: 0 5px 15px rgba(220, 53, 69, 0.4);
            }

            .ripple {
              position: absolute;
              width: 100%;
              height: 100%;
              background: rgba(220, 53, 69, 0.2);
              border-radius: 50%;
              z-index: 1;
              animation: ripple 2s ease-out infinite;
            }

            .ripple:nth-child(2) {
              animation-delay: 0.5s;
            }

            .ripple:nth-child(3) {
              animation-delay: 1s;
            }

            .retry-btn {
              position: relative;
              overflow: hidden;
              transition: all 0.3s ease;
            }

            .retry-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 7px 20px rgba(220, 53, 69, 0.3);
            }

            .retry-btn:active {
              transform: translateY(0);
            }

            .retry-btn::after {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              width: 5px;
              height: 5px;
              background: rgba(255, 255, 255, 0.5);
              opacity: 0;
              border-radius: 100%;
              transform: scale(1, 1) translate(-50%, -50%);
              transform-origin: 50% 50%;
            }

            .retry-btn:focus:not(:active)::after {
              animation: ripple 1s ease-out;
            }

            .details-summary {
              transition: all 0.3s ease;
            }

            .details-summary:hover {
              color: #dc3545;
            }

            .details-summary::marker {
              display: none;
              content: '';
            }

            .details-summary::-webkit-details-marker {
              display: none;
            }

            .details-content {
              max-height: 0;
              overflow: hidden;
              transition: max-height 0.3s ease;
            }

            details[open] .details-content {
              max-height: 300px;
            }

            .support-link {
              position: relative;
            }

            .support-link::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 0;
              width: 0;
              height: 2px;
              background: #0d6efd;
              transition: width 0.3s ease;
            }

            .support-link:hover::after {
              width: 100%;
            }
          `}</style>

          <div className="error-card">
            {/* Animated error icon */}
            <div className="error-icon-container">
              <div className="ripple"></div>
              <div className="ripple"></div>
              <div className="ripple"></div>
              <div className="error-icon">!</div>
            </div>

            {/* Title */}
            <h2
              style={{
                fontSize: '1.8rem',
                marginBottom: '1rem',
                fontWeight: 700,
                color: '#343a40',
                animation: 'fadeIn 0.6s ease-out forwards'
              }}
            >
              {this.state.errorType === 'NetworkError' 
                ? 'Connection Error' 
                : 'Something Went Wrong'}
            </h2>

            {/* Location */}
            <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
              Error occurred in: <code>{this.state.errorLocation || 'current page'}</code>
            </p>

            {/* Message */}
            <p
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.6,
                marginBottom: '2rem',
                color: '#6c757d',
                animation: 'fadeIn 0.8s ease-out forwards'
              }}
            >
              {this.getUserFriendlyMessage()}
            </p>

            {/* Error details */}
            {this.state.error && (
              <details 
                open={this.state.detailsOpen}
                onToggle={this.toggleDetails}
                style={{
                  marginBottom: '2rem',
                  width: '100%',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e9ecef'
                }}
              >
                <summary 
                  className="details-summary"
                  style={{ 
                    padding: '1rem',
                    outline: 'none',
                    fontWeight: 500,
                    color: '#495057',
                    listStyle: 'none',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>Technical Details</span>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{
                      transition: 'transform 0.3s ease',
                      transform: this.state.detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </summary>
                <div className="details-content">
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      padding: '1rem',
                      backgroundColor: '#f8f9fa',
                      margin: 0,
                      overflowY: 'auto',
                      borderTop: '1px solid #e9ecef',
                      color: '#495057',
                      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                      maxHeight: '200px'
                    }}
                  >
                    Error Type: {this.state.errorType}
                    {'\n\n'}
                    {this.state.error.toString()}
                    {this.state.error.stack && (
                      <>
                        <br />
                        <br />
                        Stack Trace:
                        <br />
                        {this.state.error.stack}
                      </>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <>
                        <br />
                        <br />
                        Component Stack:
                        <br />
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </div>
              </details>
            )}

            {/* Retry button */}
            <button
              onClick={this.handleRetry}
              className="retry-btn"
              style={{
                padding: '0.875rem 2rem',
                fontSize: '1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                boxShadow: '0 4px 6px rgba(220, 53, 69, 0.2)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                margin: '0 auto',
                animation: 'pulse 2s ease-in-out infinite'
              }}
              onMouseEnter={() => this.setState({ isHovering: true })}
              onMouseLeave={() => this.setState({ isHovering: false })}
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                  transform: this.state.isHovering ? 'rotate(360deg)' : 'rotate(0deg)',
                  transition: 'transform 0.6s ease'
                }}
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {this.state.errorType === 'NetworkError' ? 'Retry Connection' : 'Reload Application'}
            </button>

            {/* Support link */}
            <p
              style={{
                marginTop: '2rem',
                fontSize: '0.9rem',
                color: '#6c757d',
                animation: 'fadeIn 1s ease-out forwards'
              }}
            >
              Need immediate assistance?{' '}
              <a 
                href="mailto:support@example.com" 
                className="support-link"
                style={{ 
                  color: '#0d6efd', 
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                Contact Our Support Team
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// import React from "react";

// class ErrorBoundary extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = { 
//       hasError: false, 
//       error: null, 
//       errorInfo: null,
//       isHovering: false,
//       detailsOpen: false
//     };
//   }

//   static getDerivedStateFromError(error) {
//     return { hasError: true, error };
//   }

//   componentDidCatch(error, errorInfo) {
//     console.error("❌ Caught by ErrorBoundary:", error, errorInfo);
//     this.setState({ errorInfo });
//   }

//   handleRetry = () => {
//     this.setState({ hasError: false, error: null, errorInfo: null });
//     if (this.props.onRetry) {
//       this.props.onRetry();
//     } else {
//       window.location.reload();
//     }
//   };

//   toggleDetails = () => {
//     this.setState(prev => ({ detailsOpen: !prev.detailsOpen }));
//   };

//   render() {
//     if (this.state.hasError) {
//       return (
//         <div
//           style={{
//             height: '100vh',
//             inset: 0,
//             display: 'flex',
//             flexDirection: 'column',
//             alignItems: 'center',
//             justifyContent: 'center',
//             background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//             color: '#212529',
//             fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
//             padding: '2rem',
//             zIndex: 50,
//             overflow: 'hidden',
//             textAlign: 'center',
//             position: 'relative',
//           }}
//         >
//           {/* Animated background elements */}
//           <div style={{
//             position: 'absolute',
//             top: '-50px',
//             right: '-50px',
//             width: '200px',
//             height: '200px',
//             borderRadius: '50%',
//             background: 'rgba(220, 53, 69, 0.1)',
//             filter: 'blur(40px)',
//             animation: 'float 15s ease-in-out infinite alternate'
//           }}></div>
          
//           <div style={{
//             position: 'absolute',
//             bottom: '-50px',
//             left: '-50px',
//             width: '300px',
//             height: '300px',
//             borderRadius: '50%',
//             background: 'rgba(13, 110, 253, 0.1)',
//             filter: 'blur(50px)',
//             animation: 'float 20s ease-in-out infinite alternate-reverse'
//           }}></div>

//           <style>{`
//             @keyframes float {
//               0% { transform: translate(0, 0) rotate(0deg); }
//               50% { transform: translate(20px, 20px) rotate(5deg); }
//               100% { transform: translate(0, 0) rotate(0deg); }
//             }

//             @keyframes fadeIn {
//               from { opacity: 0; transform: translateY(20px); }
//               to { opacity: 1; transform: translateY(0); }
//             }

//             @keyframes pulse {
//               0%, 100% { transform: scale(1); }
//               50% { transform: scale(1.05); }
//             }

//             @keyframes ripple {
//               0% { transform: scale(0.8); opacity: 1; }
//               100% { transform: scale(1.2); opacity: 0; }
//             }

//             .error-card {
//               background: white;
//               border-radius: 16px;
//               box-shadow: 0 10px 30px rgba(0,0,0,0.1);
//               padding: 40px;
//               max-width: 600px;
//               width: 90%;
//               position: relative;
//               overflow: hidden;
//               z-index: 10;
//               transition: all 0.3s ease;
//               border: 1px solid rgba(0,0,0,0.05);
//             }

//             .error-card:hover {
//               transform: translateY(-5px);
//               box-shadow: 0 15px 35px rgba(0,0,0,0.15);
//             }

//             .error-icon-container {
//               position: relative;
//               width: 100px;
//               height: 100px;
//               margin: 0 auto 25px;
//             }

//             .error-icon {
//               width: 100%;
//               height: 100%;
//               display: flex;
//               align-items: center;
//               justify-content: center;
//               background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
//               color: white;
//               border-radius: 50%;
//               font-size: 40px;
//               font-weight: bold;
//               position: relative;
//               z-index: 2;
//               box-shadow: 0 5px 15px rgba(220, 53, 69, 0.4);
//             }

//             .ripple {
//               position: absolute;
//               width: 100%;
//               height: 100%;
//               background: rgba(220, 53, 69, 0.2);
//               border-radius: 50%;
//               z-index: 1;
//               animation: ripple 2s ease-out infinite;
//             }

//             .ripple:nth-child(2) {
//               animation-delay: 0.5s;
//             }

//             .ripple:nth-child(3) {
//               animation-delay: 1s;
//             }

//             .retry-btn {
//               position: relative;
//               overflow: hidden;
//               transition: all 0.3s ease;
//             }

//             .retry-btn:hover {
//               transform: translateY(-2px);
//               box-shadow: 0 7px 20px rgba(220, 53, 69, 0.3);
//             }

//             .retry-btn:active {
//               transform: translateY(0);
//             }

//             .retry-btn::after {
//               content: '';
//               position: absolute;
//               top: 50%;
//               left: 50%;
//               width: 5px;
//               height: 5px;
//               background: rgba(255, 255, 255, 0.5);
//               opacity: 0;
//               border-radius: 100%;
//               transform: scale(1, 1) translate(-50%, -50%);
//               transform-origin: 50% 50%;
//             }

//             .retry-btn:focus:not(:active)::after {
//               animation: ripple 1s ease-out;
//             }

//             .details-summary {
//               transition: all 0.3s ease;
//             }

//             .details-summary:hover {
//               color: #dc3545;
//             }

//             .details-summary::marker {
//               display: none;
//               content: '';
//             }

//             .details-summary::-webkit-details-marker {
//               display: none;
//             }

//             .details-content {
//               max-height: 0;
//               overflow: hidden;
//               transition: max-height 0.3s ease;
//             }

//             details[open] .details-content {
//               max-height: 300px;
//             }

//             .support-link {
//               position: relative;
//             }

//             .support-link::after {
//               content: '';
//               position: absolute;
//               bottom: -2px;
//               left: 0;
//               width: 0;
//               height: 2px;
//               background: #0d6efd;
//               transition: width 0.3s ease;
//             }

//             .support-link:hover::after {
//               width: 100%;
//             }
//           `}</style>

//           <div className="error-card">
//             {/* Animated error icon */}
//             <div className="error-icon-container">
//               <div className="ripple"></div>
//               <div className="ripple"></div>
//               <div className="ripple"></div>
//               <div className="error-icon">!</div>
//             </div>

//             {/* Title */}
//             <h2
//               style={{
//                 fontSize: '1.8rem',
//                 marginBottom: '1rem',
//                 fontWeight: 700,
//                 color: '#343a40',
//                 animation: 'fadeIn 0.6s ease-out forwards'
//               }}
//             >
//               Something Went Wrong
//             </h2>

//             {/* Message */}
//             <p
//               style={{
//                 fontSize: '1.1rem',
//                 lineHeight: 1.6,
//                 marginBottom: '2rem',
//                 color: '#6c757d',
//                 animation: 'fadeIn 0.8s ease-out forwards'
//               }}
//             >
//               We've encountered an unexpected error. Our team has been notified and we're working to fix it.
//             </p>

//             {/* Error details */}
//             {this.state.error && (
//               <details 
//                 open={this.state.detailsOpen}
//                 onToggle={this.toggleDetails}
//                 style={{
//                   marginBottom: '2rem',
//                   width: '100%',
//                   backgroundColor: '#ffffff',
//                   borderRadius: '8px',
//                   cursor: 'pointer',
//                   transition: 'all 0.3s ease',
//                   border: '1px solid #e9ecef'
//                 }}
//               >
//                 <summary 
//                   className="details-summary"
//                   style={{ 
//                     padding: '1rem',
//                     outline: 'none',
//                     fontWeight: 500,
//                     color: '#495057',
//                     listStyle: 'none',
//                     position: 'relative',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'space-between'
//                   }}
//                 >
//                   <span>Technical Details</span>
//                   <svg 
//                     width="16" 
//                     height="16" 
//                     viewBox="0 0 24 24" 
//                     fill="none" 
//                     stroke="currentColor" 
//                     strokeWidth="2" 
//                     strokeLinecap="round" 
//                     strokeLinejoin="round"
//                     style={{
//                       transition: 'transform 0.3s ease',
//                       transform: this.state.detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
//                     }}
//                   >
//                     <polyline points="6 9 12 15 18 9"></polyline>
//                   </svg>
//                 </summary>
//                 <div className="details-content">
//                   <pre
//                     style={{
//                       whiteSpace: 'pre-wrap',
//                       wordBreak: 'break-word',
//                       textAlign: 'left',
//                       fontSize: '0.85rem',
//                       padding: '1rem',
//                       backgroundColor: '#f8f9fa',
//                       margin: 0,
//                       overflowY: 'auto',
//                       borderTop: '1px solid #e9ecef',
//                       color: '#495057',
//                       fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
//                       maxHeight: '200px'
//                     }}
//                   >
//                     {this.state.error.toString()}
//                     {this.state.errorInfo && (
//                       <>
//                         <br />
//                         <br />
//                         {this.state.errorInfo.componentStack}
//                       </>
//                     )}
//                   </pre>
//                 </div>
//               </details>
//             )}

//             {/* Retry button */}
//             <button
//               onClick={this.handleRetry}
//               className="retry-btn"
//               style={{
//                 padding: '0.875rem 2rem',
//                 fontSize: '1rem',
//                 backgroundColor: '#dc3545',
//                 color: 'white',
//                 border: 'none',
//                 borderRadius: '8px',
//                 fontWeight: 600,
//                 boxShadow: '0 4px 6px rgba(220, 53, 69, 0.2)',
//                 transition: 'all 0.3s ease',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 gap: '0.5rem',
//                 cursor: 'pointer',
//                 margin: '0 auto',
//                 animation: 'pulse 2s ease-in-out infinite'
//               }}
//               onMouseEnter={() => this.setState({ isHovering: true })}
//               onMouseLeave={() => this.setState({ isHovering: false })}
//             >
//               <svg 
//                 width="18" 
//                 height="18" 
//                 viewBox="0 0 24 24" 
//                 fill="none" 
//                 stroke="currentColor" 
//                 strokeWidth="2" 
//                 strokeLinecap="round" 
//                 strokeLinejoin="round"
//                 style={{
//                   transform: this.state.isHovering ? 'rotate(360deg)' : 'rotate(0deg)',
//                   transition: 'transform 0.6s ease'
//                 }}
//               >
//                 <polyline points="23 4 23 10 17 10"></polyline>
//                 <polyline points="1 20 1 14 7 14"></polyline>
//                 <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
//               </svg>
//               Reload Application
//             </button>

//             {/* Support link */}
//             <p
//               style={{
//                 marginTop: '2rem',
//                 fontSize: '0.9rem',
//                 color: '#6c757d',
//                 animation: 'fadeIn 1s ease-out forwards'
//               }}
//             >
//               Need immediate assistance?{' '}
//               <a 
//                 href="mailto:support@example.com" 
//                 className="support-link"
//                 style={{ 
//                   color: '#0d6efd', 
//                   textDecoration: 'none',
//                   fontWeight: 500,
//                 }}
//               >
//                 Contact Our Support Team
//               </a>
//             </p>
//           </div>
//         </div>
//       );
//     }

//     return this.props.children;
//   }
// }

// export default ErrorBoundary;

// import React from "react";

// class ErrorBoundary extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = { hasError: false, error: null };
//   }

//   static getDerivedStateFromError(error) {
//     // Update state so next render shows fallback UI
//     return { hasError: true, error };
//   }

//   componentDidCatch(error, errorInfo) {
//     console.error("❌ Caught by ErrorBoundary:", error, errorInfo);
//     // Optional: Log error to external service
//   }

//   handleRetry = () => {
//     // Full page reload to retry everything
//     window.location.reload();
//   };

//   render() {
//     if (this.state.hasError) {
//       return (
//         <div style={{ padding: "20px", textAlign: "center", color: "#b00" }}>
//           <h2>😅 Something went wrong.</h2>
//           <p>Don't worry — we've noted the issue.</p>
//           <button
//             onClick={this.handleRetry}
//             style={{
//               marginTop: "16px",
//               padding: "10px 20px",
//               fontSize: "16px",
//               backgroundColor: "#007bff",
//               color: "#fff",
//               border: "none",
//               borderRadius: "4px",
//               cursor: "pointer",
//             }}
//           >
//             🔄 Retry
//           </button>
//         </div>
//       );
//     }

//     return this.props.children;
//   }
// }

// export default ErrorBoundary;
